import { db } from '../lib/firebase';
import { 
  collection, 
  addDoc, 
  query, 
  orderBy, 
  limit, 
  getDocs, 
  serverTimestamp,
  where,
  deleteDoc,
  doc
} from 'firebase/firestore';

// Database structure: vindie/{collection}

export interface Comment {
  id?: string;
  contentId: string;
  userId: string;
  userName: string;
  userAvatar?: string;
  text: string;
  createdAt: any;
  updatedAt?: any;
  likes?: number;
  dislikes?: number;
  replies?: Comment[];
  parentId?: string;
}

export async function addComment(contentId: string, userId: string, userName: string, userAvatar: string | undefined, text: string, parentId?: string) {
  // Import sanitization utilities
  const { sanitizeText } = await import('../utils/inputSanitizer');
  
  // Sanitize user inputs
  const sanitizedText = sanitizeText(text, 2000); // Max 2000 chars for comments
  const sanitizedUserName = sanitizeText(userName, 100);
  const sanitizedParentId = parentId ? sanitizeText(parentId, 50) : undefined;
  
  if (!sanitizedText.trim()) {
    throw new Error('Comment text cannot be empty');
  }
  
  const col = collection(db, 'comments');
  const commentData: Omit<Comment, 'id'> = {
    contentId: sanitizeText(contentId, 100),
    userId: sanitizeText(userId, 100),
    userName: sanitizedUserName,
    userAvatar: userAvatar ? sanitizeText(userAvatar, 500) : undefined,
    text: sanitizedText,
    createdAt: serverTimestamp(),
    likes: 0,
    dislikes: 0,
  };
  
  // Only add parentId if it's provided (for replies)
  if (sanitizedParentId) {
    commentData.parentId = sanitizedParentId;
  }
  
  return await addDoc(col, commentData);
}

export async function getComments(contentId: string, limitCount = 50): Promise<Comment[]> {
  const q = query(
    collection(db, 'comments'),
    where('contentId', '==', contentId),
    where('parentId', '==', null),
    orderBy('createdAt', 'desc'),
    limit(limitCount)
  );
  
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as Comment));
}

export async function getReplies(parentId: string): Promise<Comment[]> {
  const q = query(
    collection(db, 'comments'),
    where('parentId', '==', parentId),
    orderBy('createdAt', 'asc')
  );
  
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as Comment));
}

export async function deleteComment(commentId: string) {
  const ref = doc(db, 'comments', commentId);
  await deleteDoc(ref);
}
